import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async'; // eslint-disable-line no-unused-vars
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SEO } from '../../components/SEO';
import { NewsletterSignup } from '../../components/NewsletterSignup';
import {
  ArrowRight, Trophy, Users, Sparkles, CheckCircle2, Rocket, Award, Heart,
  Star, GraduationCap, FlaskConical, Code, Bot, Cog, Calculator, Sun,
  Package, Clock, MapPin, ChevronDown, BookOpen, Shield, Brain, Zap, UserPlus,
  Quote, ChevronLeft, ChevronRight, Mail
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEM_PILLARS = [
  { label: 'Science', href: '/science', icon: FlaskConical, color: 'from-rose-500 to-pink-400', desc: 'Lab experiments, DNA extraction, chemistry — real science with real kits' },
  { label: 'Technology', href: '/technology', icon: Code, color: 'from-blue-500 to-indigo-400', desc: 'Scratch to Python, web dev, AI, cybersecurity — code the future' },
  { label: 'Engineering', href: '/engineering', icon: Cog, color: 'from-emerald-500 to-teal-400', desc: 'CAD design, 3D printing, bridge building — make real things' },
  { label: 'Math', href: '/math', icon: Calculator, color: 'from-purple-500 to-violet-400', desc: 'AMC, MATHCOUNTS, Math Olympiad — competition-ready problem solving' },
  { label: 'Robotics', href: '/programs/robotics', icon: Bot, color: 'from-orange-500 to-amber-400', desc: 'LEGO, FANUC, FRC — from first build to career certification' },
];

const STATS = [
  { value: '12,000+', label: 'Learners Served', icon: Users },
  { value: '5', label: 'STEM Divisions', icon: Sparkles },
  { value: 'Top 5%', label: 'STEM.org Accredited', icon: Award },
  { value: '28+', label: 'Partner Schools', icon: MapPin },
  { value: '4.9★', label: 'Sawyer Reviews', icon: Star },
  { value: 'FREE', label: 'For Qualifying Families', icon: Heart },
];

export default function HomePage() {
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/reviews/featured/`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setFeaturedReviews(data); })
      .catch(() => {});
  }, []);

  return (
    <div className="overflow-hidden" data-testid="homepage">
      <SEO title="K-8 STEM Education" description="STEM.org accredited K-8 STEM education. Science, Technology, Engineering, Math & Robotics courses and summer camps in Aurora, Naperville & Fox Valley IL. 12,000+ learners served." />
      {/* WebSite JSON-LD schema lives in public/index.html so crawlers + View Source
          see it on first byte. Helmet duplicate removed (May 15, 2026). */}

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-rsc-navy">
        {/*
          Background video (replaces the right-side photo collage + STEM pillar
          icons + pillars card; May-21 user request).
          - poster=<jpg>           — shown immediately, kills hero LCP wait
          - autoplay muted loop    — required combo for browser autoplay
          - playsinline            — keeps iOS Safari from fullscreening
          - preload="metadata"     — fetches only ~150KB until in-view
          - aria-hidden            — decorative, screen readers skip it
          The dark overlay (next sibling) restores legibility for the white copy
          on top of the brighter frames.
        */}
        <video
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          src="https://res.cloudinary.com/dfa8raimq/video/upload/v1779392937/Timeline_15_ztqx0z.mp4"
          poster="https://res.cloudinary.com/dfa8raimq/image/upload/v1779393374/_28A5975_2_hj5qj3.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="hero-video"
        />
        {/* Readability overlay — dark gradient bottom-left so text never washes out. */}
        <div className="absolute inset-0 bg-gradient-to-r from-rsc-navy/85 via-rsc-navy/65 to-rsc-navy/40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-rsc-navy/70 via-transparent to-transparent pointer-events-none" />

        <div className="container-app relative z-10 py-10 md:py-14">
          {/* Two-column layout on lg+: text left, promo card + stats right.
              On mobile: single column (text → promo → stats) for natural reading.
              max-w-7xl (containers cap at 80rem) so we use as much width as
              the page chrome allows without bleeding into the gutter. */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-12 items-center">
            {/* LEFT — Headline / sub / trust line */}
            <div className="flex flex-col">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-5 self-start">
                <Trophy className="w-4 h-4" /> Best In STEM™ — Top 5% Worldwide
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-5 tracking-tight">
                5 STEM Pillars.<br />1 Platform.<br />
                <span className="text-rsc-yellow">Limitless Potential.</span>
              </h1>
              <p className="text-base sm:text-lg text-white/85 max-w-2xl mb-3 leading-relaxed drop-shadow">
                Science · Technology · Engineering · Math · Robotics — hands-on, kit-included
                programs that build real skills from kindergarten through career.
              </p>
              {/* Credibility line — May-21 user request removed the "12,000+ learners ·
                  28 partner schools ·" prefix; kept the location + APS attribution which
                  still reinforce trust without competing with the new background video. */}
              <p className="text-base sm:text-lg text-white/95 font-semibold mb-3 leading-relaxed flex flex-wrap items-center gap-x-2 gap-y-1 drop-shadow">
                <span className="text-white/85">Aurora, Naperville &amp; Fox Valley</span>
                <span className="text-rsc-yellow/60">·</span>
                <span className="text-white/85">Powered by</span>
                <span className="text-rsc-yellow font-extrabold">APS Training Academy 501(c)(3)</span>
              </p>
              <p className="text-sm sm:text-base text-rsc-yellow/85 font-semibold italic">Formerly Ready Set Code</p>
            </div>

            {/* RIGHT — Summer Camp promo + 6 stat tiles. Stacks below on mobile. */}
            <div className="flex flex-col gap-5">
              <Link to="/summer-camps" className="block">
                <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-gray-900/90 to-gray-800/90 border-2 border-rsc-yellow/40 p-5 sm:p-6 hover:border-rsc-yellow/60 transition-all group shadow-2xl hover:shadow-rsc-yellow/20 backdrop-blur-sm flex items-center">
                  <div className="flex items-center gap-4 sm:gap-5 w-full">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 shadow-xl">
                      <img
                        src="https://res.cloudinary.com/dfa8raimq/image/upload/v1779395695/IMG_0509_zfjer7.jpg"
                        alt="Summer Camp STEM Activities"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-rsc-yellow animate-pulse" />
                        <span className="text-xs sm:text-sm font-extrabold text-rsc-yellow uppercase tracking-wider">Summer 2026</span>
                      </div>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-extrabold text-white mb-1.5 leading-tight">
                        8 Weeks of STEM Excellence!
                      </h3>
                      <p className="text-xs sm:text-sm text-white/65 leading-snug">
                        Browse camps by week • Grades K-8 • Starting from $45/Session (3 hours)
                      </p>
                    </div>
                    <ArrowRight className="hidden sm:block w-7 h-7 text-rsc-yellow group-hover:translate-x-2 transition-transform flex-shrink-0" />
                  </div>
                  <div className="absolute -inset-1 bg-gradient-to-r from-rsc-yellow/20 to-orange-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10"></div>
                </div>
              </Link>

              {/* Stat tiles — 3×2 on mobile, 3×2 on desktop too (the grid fills the
                  narrower right column more naturally as 3 columns vs 6 wide). */}
              <div className="grid grid-cols-3 gap-2.5">
                {STATS.map((stat, i) => (
                  <div key={i} className="px-3 py-3 rounded-xl bg-white/10 border border-white/15 text-center backdrop-blur-sm">
                    <stat.icon className="w-4 h-4 text-rsc-yellow/80 mx-auto mb-1" />
                    <p className="text-lg sm:text-xl font-extrabold text-white leading-none">{stat.value}</p>
                    <p className="text-[10px] sm:text-[11px] text-white/60 mt-1 leading-tight">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══════════ SUMMER CAMP COUNTDOWN BANNER ═══════════ */}
      <section className="relative bg-gradient-to-r from-orange-500 via-rsc-yellow to-orange-500 py-4">
        <div className="container-app">
          <div className="flex flex-wrap items-center justify-center gap-3 text-rsc-navy">
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 animate-pulse" />
              <span className="font-bold text-sm sm:text-base">
                Summer 2026 Registration Open!
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <Clock className="w-4 h-4" />
              <span>Jun 8 - Aug 7 · 8 Weeks · Grades K-8</span>
            </div>
            <Link to="/summer-camps">
              <Button size="sm" className="bg-rsc-navy hover:bg-rsc-navy/90 text-white font-semibold">
                Browse Summer Camps <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ 5 STEM PILLARS ═══════════ */}
      <section className="relative py-14 bg-gradient-to-b from-[#0f2847] to-rsc-navy">
        <div className="container-app">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" /> OUR 5 STEM DIVISIONS
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              Every Pillar of <span className="text-rsc-yellow">STEM Education</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm">
              Not just coding. A complete STEM education platform — each division with its own curriculum, kits, and career pathways.
            </p>
          </div>

          {/* Responsive grid: 2 columns on tight phones (Robotics wraps to its
              own row beneath), 3 columns at sm:, 5 columns on lg:+ desktop.
              Card paddings + icon size step up with the breakpoint so a 2-col
              tile doesn't waste vertical space, and the desktop tile stays
              substantial. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {STEM_PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <Link key={i} to={pillar.href} className="group">
                  <div className="h-full rounded-xl bg-white/[0.04] border border-white/[0.08] p-3 sm:p-4 lg:p-5 text-center hover:bg-white/[0.08] hover:border-white/[0.15] transition-all group-hover:scale-[1.02]">
                    <div className={`w-11 h-11 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br ${pillar.color} flex items-center justify-center mx-auto mb-2 lg:mb-3 shadow-lg group-hover:shadow-xl transition-shadow`}>
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-white mb-1">{pillar.label}</h3>
                    <p className="text-[11px] text-white/45 leading-snug mb-2 lg:mb-3 line-clamp-3">{pillar.desc}</p>
                    <span className="inline-flex items-center gap-1 text-rsc-yellow text-xs font-semibold group-hover:gap-2 transition-all">
                      Explore <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>


      {/* ═══════════ WHY READY SET STEM ═══════════ */}
      <section className="relative py-14 bg-gradient-to-b from-rsc-navy to-[#0f2847]">
        <div className="container-app">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Award className="w-3.5 h-3.5" /> WHY PARENTS CHOOSE US
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              The STEM Education <span className="text-rsc-yellow">Parents Trust</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {[
              { icon: Award, title: 'STEM.org Accredited — Top 5%', desc: 'Dual Best In STEM™ + Accredited™ Trustmark. A distinction held by only the top 5% of STEM programs worldwide.', color: 'text-amber-400 bg-amber-500/15' },
              { icon: Package, title: 'Real Kits — Take Them Home', desc: 'Every course ships with a hands-on STEM kit. Real lab equipment, real tools, real experiments. The kit is yours to keep.', color: 'text-emerald-400 bg-emerald-500/15' },
              { icon: Users, title: 'Small Classes, Expert Instructors', desc: 'Max 24 students per session with trained, background-checked STEM instructors. Not babysitting — real education.', color: 'text-blue-400 bg-blue-500/15' },
              { icon: GraduationCap, title: 'Career Pathways Built In', desc: 'Every program maps to real careers — from Science Olympiad to FANUC certification to college-ready portfolios.', color: 'text-purple-400 bg-purple-500/15' },
              { icon: Heart, title: 'FREE for Qualifying Families', desc: '93% of our learners attend free through APS Training Academy 501(c)(3). Quality STEM education shouldn\'t depend on income.', color: 'text-rose-400 bg-rose-500/15' },
              { icon: Sparkles, title: 'Gamified & Fun', desc: 'Points, badges, STEM Readiness Score, blockchain certificates — students earn rewards as they learn and stay motivated.', color: 'text-orange-400 bg-orange-500/15' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="p-5 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] transition-colors">
                  <div className={`w-11 h-11 rounded-xl ${item.color.split(' ')[1]} flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${item.color.split(' ')[0]}`} />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs text-white/40 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ COMMUNITY IMPACT ═══════════ */}
      <section className="relative py-14 bg-[#0f2847]">
        <div className="container-app">
          <div className="rounded-2xl bg-white/[0.04] border border-white/[0.08] p-8 sm:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
                  <Heart className="w-3.5 h-3.5" /> COMMUNITY IMPACT
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                  STEM Education <span className="text-rsc-yellow">For Every Child</span>
                </h2>
                <p className="text-sm text-white/50 mb-6 leading-relaxed">
                  Powered by APS Training Academy, a 501(c)(3) non-profit. We ensure every child
                  can access quality STEM education — regardless of income. 93% of our learners attend free.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {[
                    { value: '93%', label: 'Students below AMI' },
                    { value: '28', label: 'Partner schools' },
                    { value: '49%', label: 'Female/non-binary' },
                    { value: 'FREE', label: 'For qualifying families' },
                  ].map((s, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white/[0.05] border border-white/[0.06] text-center">
                      <p className="text-lg font-extrabold text-rsc-yellow">{s.value}</p>
                      <p className="text-[10px] text-white/35">{s.label}</p>
                    </div>
                  ))}
                </div>
                <Link to="/community-impact">
                  <Button className="bg-rsc-yellow text-rsc-navy font-bold rounded-full px-6 py-5 text-sm">
                    Learn About Our Impact <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="rounded-xl overflow-hidden aspect-square hidden lg:block">
                <img
                  src="https://res.cloudinary.com/dfa8raimq/image/upload/v1773928567/19_c9xtz2.jpg"
                  alt="Children learning STEM"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <section className="relative py-14 bg-rsc-navy">
        <div className="container-app">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              Getting Started Is <span className="text-rsc-yellow">Easy</span>
            </h2>
          </div>
          {/* 2-col on phones (4 steps wrap to 2 rows of 2), 4-col on md:+.
              Avoids the giant 1-col stack on mobile. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'Create Account', desc: 'Sign up and add your children', icon: UserPlus, color: 'from-blue-500 to-indigo-400' },
              { step: '2', title: 'Browse Programs', desc: 'Explore 5 STEM divisions + summer camps', icon: BookOpen, color: 'from-emerald-500 to-teal-400' },
              { step: '3', title: 'Register & Pay', desc: 'Secure checkout with Stripe', icon: Shield, color: 'from-purple-500 to-violet-400' },
              { step: '4', title: 'Learn & Grow', desc: 'Earn badges, points, and certificates', icon: Rocket, color: 'from-orange-500 to-amber-400' },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs text-white/40">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ PARENT TESTIMONIALS ═══════════ */}
      {featuredReviews.length > 0 && (
        <section className="relative py-14 bg-gradient-to-b from-rsc-navy to-[#142f5a]" data-testid="testimonials-section">
          <div className="container-app">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
                <Heart className="w-3.5 h-3.5" /> LOVED BY FAMILIES
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
                What Parents Are <span className="text-rsc-yellow">Saying</span>
              </h2>
            </div>
            
            <div className="relative max-w-2xl mx-auto">
              <div className="bg-white/[0.06] backdrop-blur-sm rounded-2xl border border-white/10 p-8" data-testid="testimonial-card">
                <Quote className="w-8 h-8 text-rsc-yellow/40 mb-4" />
                <p className="text-lg text-white/80 mb-4 leading-relaxed italic">
                  "{featuredReviews[reviewIdx]?.comment}"
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex gap-0.5 mb-1">
                      {[1,2,3,4,5].map(s => (
                        <Star key={s} className={`w-4 h-4 ${s <= featuredReviews[reviewIdx]?.rating ? 'fill-amber-400 text-amber-400' : 'text-white/20'}`} />
                      ))}
                    </div>
                    <p className="text-sm font-medium text-white/60">
                      {featuredReviews[reviewIdx]?.user_name}
                      {featuredReviews[reviewIdx]?.title && <span className="text-white/30"> — {featuredReviews[reviewIdx]?.title}</span>}
                    </p>
                  </div>
                  {featuredReviews.length > 1 && (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setReviewIdx(i => (i - 1 + featuredReviews.length) % featuredReviews.length)}
                        className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition"
                        data-testid="testimonial-prev"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => setReviewIdx(i => (i + 1) % featuredReviews.length)}
                        className="w-8 h-8 rounded-full border border-white/15 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition"
                        data-testid="testimonial-next"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {featuredReviews.length > 1 && (
                <div className="flex justify-center gap-1.5 mt-4">
                  {featuredReviews.map((_, i) => (
                    <button key={i} onClick={() => setReviewIdx(i)} className={`w-2 h-2 rounded-full transition ${i === reviewIdx ? 'bg-rsc-yellow' : 'bg-white/20'}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ NEWSLETTER SIGNUP ═══════════ */}
      <section className="relative py-12 bg-[#0f2847]" data-testid="newsletter-section">
        <div className="container-app">
          <div className="max-w-xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Mail className="w-3.5 h-3.5" /> FREE STEM NEWSLETTER
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-2">
              Weekly STEM Tips for Parents
            </h2>
            <p className="text-sm text-white/45 mb-5">
              Get activity ideas, camp updates, and exclusive discounts delivered to your inbox.
            </p>
            <div className="flex justify-center">
              <NewsletterSignup source="homepage" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ BOTTOM CTA ═══════════ */}
      <section className="relative py-14 bg-gradient-to-b from-rsc-navy to-[#0f2847]">
        <div className="container-app text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3">
            Ready to start your child's STEM journey?
          </h2>
          <p className="text-sm text-white/45 mb-6 max-w-md mx-auto">
            Join 12,000+ families who've discovered the power of hands-on STEM education.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {/* Browse All Courses button removed (May 21, 2026 user request) —
                the Summer Camps + Create Account CTAs are the primary intents. */}
            <Link to="/summer-camps">
              <Button variant="outline" className="border-rsc-yellow/30 text-rsc-yellow hover:bg-rsc-yellow/10 text-sm px-8 py-5 font-bold rounded-full">
                <Sun className="w-4 h-4 mr-2" /> Summer Camps 2026
              </Button>
            </Link>
            {/* /register is the COURSE-registration page (requires ?type&id query params).
                Account creation lives at /auth/register. Previously pointed to /register
                which surfaced "Invalid registration link" then redirected home. */}
            <Link to="/auth/register">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 text-sm px-8 py-5 rounded-full">
                Create Account
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
